/**
 * Belt-and-braces polling for `useInterviewState` (#553).
 *
 * Two refetch paths feed the renderer's plan-mode view:
 *
 *   1. FAST PATH — `interview:plan-updated` push. The main process's
 *      fs.watch on `<gamePath>/.foundry/build-game-interview.json`
 *      observes SKILL writes and pushes a notification; the renderer
 *      calls `refetch()` on the state query. Sub-100ms when it works.
 *
 *   2. SAFETY NET — this hook. A 3s setInterval that fires the SAME
 *      `refetch()` whenever the interview is non-terminal. Catches:
 *        * fs.watch race on first read (watcher armed AFTER the SKILL
 *          write the user is waiting on — Crucible #553 fix awaits
 *          the watcher arm before reading, but cross-process timing
 *          edges can still slip).
 *        * Atomic-rename event coalescing — the SKILL writes via
 *          temp-file + rename; some Windows/macOS filesystems fire
 *          'rename' events with a different filename or coalesce
 *          the change-then-rename pair into one event the watcher's
 *          filename filter drops.
 *        * Push-channel hiccup — renderer reloaded mid-flight, IPC
 *          bridge unsubscribed, contextBridge promise rejection, etc.
 *
 * Pre-#553 the polling effect lived inline in `pages/Workspace.tsx`
 * and gated on `depth === 'quick' && subPhase in [plan, refine,
 * back-to-plan, committed, build]`. That narrow gate had a fatal
 * hole: when the renderer's first read returned a state with no
 * `depth` field (the engine writes the fresh state file with a
 * full-flavoured shape; the SKILL flips `depth` to `'quick'` later),
 * `getEffectiveInterviewDepth` folded the absence to `'full'`. The
 * gate then short-circuited, and the SKILL's eventual `depth:
 * 'quick'` write was visible ONLY via the fast push. When the push
 * missed — exactly the failure mode #553 documents — the renderer
 * was stuck on the wrong view with no Start Building button.
 *
 * Post-#553 the gate is status-driven via
 * `shouldPollInterviewState` (see `pages/Workspace.tsx`): poll
 * whenever the interview status is non-terminal or absent. This
 * mirrors the existing `useInterviewStatus` refetch-interval policy
 * onto the state query.
 *
 * Lifted out of `pages/Workspace.tsx` (the same lift-the-effect-into-
 * a-hook pattern used across the Workspace effects) so:
 *   1. The test exercises the SAME effect production runs; a
 *      regression that deletes the production wiring fails the
 *      test (rather than a mirrored harness leaving us green).
 *   2. The Workspace call site is a single `useInterviewStatePoll(...)`
 *      line, making the contract auditable.
 */

import { useEffect } from 'react';
import type { PersistedBuildGameInterviewStatus } from '@volley/crucible-ipc-types/build-game-types';
import { shouldPollInterviewState, INTERVIEW_STATE_LIVE_POLL_MS } from '@volley/crucible-ipc-types/interview-poll-gate';

export interface UseInterviewStatePollInputs {
  /** Active route's gameId. Pass `undefined` when no route is mounted. */
  readonly gameId: string | undefined;
  /**
   * Status from the LATEST interview-state query result. Wide
   * (`PersistedBuildGameInterviewStatus`) so unknown future SKILL
   * terminals keep polling — defaulting to "keep polling" is what
   * makes the GUI wake up on ahead-of-GUI SKILL releases.
   */
  readonly status: PersistedBuildGameInterviewStatus | undefined;
  /**
   * Referentially stable refetch function from `useInterviewState`'s
   * `useCallback` wrapper. If a fresh identity is passed each render,
   * the interval is torn down and re-created on every Workspace
   * render — under heavy chat-stream traffic the interval can keep
   * being cleared before its 3 s tick fires and the safety net never
   * actually fires (Codex round-4 HIGH-3 on PR #544).
   */
  readonly refetch: () => void;
  /** Test override for the polling cadence. Defaults to 3 s. */
  readonly intervalMs?: number;
}

/**
 * Install the polling safety net. Returns `void` — the effect runs
 * as a side effect of rendering.
 */
export function useInterviewStatePoll(inputs: UseInterviewStatePollInputs): void {
  const { gameId, status, refetch, intervalMs } = inputs;
  const cadence = intervalMs ?? INTERVIEW_STATE_LIVE_POLL_MS;

  useEffect(() => {
    if (!gameId) return;
    if (!shouldPollInterviewState({ status })) return;
    const intervalId = setInterval(() => {
      refetch();
    }, cadence);
    return () => clearInterval(intervalId);
  }, [gameId, status, refetch, cadence]);
}
